@use '~/theme/theme' as *;
@use '~/theme/typography';

.button {
  font-family: 'Public Sans', sans-serif;
  display: inline-flex;
  align-items: center;
  position: relative;

  @include border-radius(sm);

  cursor: pointer;
  text-align: center;
  outline: 0;
  outline-color: transparent;
  text-decoration: none;
  transition:
    background-color 300ms,
    color 300ms,
    border-color 300ms,
    outline-color 300ms;

  --button-background: #{color(grey-800)};
  --button-color: white;
  --button-border: none;
  --button-background-hovered: #{color(violet-600)};
  --button-color-hovered: white;
  --button-border-hovered: none;
  --button-background-disabled: #{color(grey-500)};
  --button-color-disabled: white;
  --button-border-disabled: none;

  background-color: var(--button-background);
  color: var(--button-color);
  border: var(--button-border);

  &.confirm {
    --button-background: #{color(green-800)};
    --button-background-hovered: color-mix(in srgb, var(--button-background) 100%, #{color(grey-800)} 16%);
  }

  &.destructive {
    --button-background: #{color(lobster-800)};
    --button-background-hovered: color-mix(in srgb, var(--button-background) 100%, #{color(grey-800)} 16%);
  }

  &[data-focus-visible],
  &[data-focused][data-auto-focus] {
    outline: 2px solid color(violet-600);
    outline-offset: 2px;
  }

  &[data-hovered] {
    background: var(--button-background-hovered);
    color: var(--button-color-hovered);
    border: var(--button-border-hovered);
  }

  &[data-disabled]:not(.loading) {
    background-color: var(--button-background-disabled);
    color: var(--button-color-disabled);
    border: var(--button-border-disabled);
    cursor: default;
  }
}

.content {
  min-width: spacing(2.5);
  width: 100%;
}

.spinner {
  display: none;
  position: absolute;
  left: 50%;
  top: 50%;
  translate: -50% -50%;
}

.loading {
  pointer-events: none;

  .content {
    visibility: hidden;
  }

  .spinner {
    display: unset;
  }
}

.fullWidth {
  width: 100%;
}

.outlined {
  --button-background: transparent;
  --button-color: #{color(grey-800)};
  --button-border: #{border(grey-800)};
  --button-background-hovered: #{color(violet-100)};
  --button-color-hovered: #{color(violet-600)};
  --button-border-hovered: #{border(violet-600)};
  --button-background-disabled: white;
  --button-color-disabled: #{color(grey-500)};
  --button-border-disabled: #{border(grey-500)};

  &.confirm {
    --button-background: transparent;
    --button-color: #{color(green-800)};
    --button-border: #{border(green-800)};
    --button-background-hovered: #{color(green-100)};
    --button-color-hovered: #{color(green-800)};
    --button-border-hovered: #{border(green-800)};
  }

  &.destructive {
    --button-background: transparent;
    --button-color: #{color(lobster-800)};
    --button-border: #{border(lobster-800)};
    --button-background-hovered: #{color(lobster-100)};
    --button-border-hovered: #{border(lobster-700)};
    --button-color-hovered: #{color(lobster-800)};
  }
}

.ghost {
  --button-background: transparent;
  --button-color: #{color(grey-800)};
  --button-border: none;
  --button-background-hovered: #{color(violet-100)};
  --button-color-hovered: #{color(grey-800)};
  --button-border-hovered: none;
  --button-background-disabled: transparent;
  --button-color-disabled: #{color(grey-500)};
  --button-border-disabled: none;

  &.confirm {
    --button-background: transparent;
    --button-color: #{color(green-800)};
    --button-background-hovered: #{color(green-100)};
    --button-color-hovered: #{color(green-800)};
  }

  &.destructive {
    --button-background: transparent;
    --button-color: #{color(lobster-800)};
    --button-background-hovered: #{color(lobster-100)};
    --button-color-hovered: #{color(lobster-800)};
  }
}

.link {
  --button-background: transparent;
  --button-color: #{color(violet-600)};
  --button-border: none;
  --button-background-hovered: #{color(violet-100)};
  --button-color-hovered: #{color(violet-600)};
  --button-boder-hovered: none;
  --button-background-disabled: transparent;
  --button-color-disabled: #{color(grey-500)};
  --button-border-disabled: none;
  --button-hover-outline: #{color(violet-100)};

  &.confirm {
    --button-background: transparent;
    --button-color: #{color(green-800)};
    --button-color-hovered: #{color(green-800)};
    --button-hover-outline: #{color(green-100)};
    --button-background-hovered: #{color(green-100)};
  }

  &.destructive {
    --button-background: transparent;
    --button-color: #{color(lobster-800)};
    --button-color-hovered: #{color(lobster-800)};
    --button-hover-outline: #{color(lobster-100)};
    --button-background-hovered: #{color(lobster-100)};
  }
}

.small {
  padding: 0 spacing(1.5);
  height: 32px;

  .content {
    gap: spacing(1.5);
  }

  @include typography.caption;
  @include typography.semibold;

  &.link {
    padding: 0;
    height: auto;

    &[data-focus-visible] {
      border-radius: 2px;
    }

    &[data-hovered] {
      border-radius: 2px;
      outline: spacing(0.5) solid var(--button-hover-outline);
    }
  }

  .icon {
    @include icon-size(sm);
  }
}

.medium {
  padding: 0 spacing(2);
  height: 38px;

  .content {
    gap: spacing(1.5);
  }

  @include typography.body2;
  @include typography.semibold;

  &.link {
    padding: 0;
    height: auto;

    &[data-focus-visible] {
      border-radius: 2px;
      outline-offset: 4px;
    }

    &[data-hovered] {
      border-radius: 2px;
      outline: spacing(0.75) solid var(--button-hover-outline);
      outline-offset: 0;
    }
  }

  .icon {
    @include icon-size(sm);
  }
}

.large {
  padding: 0 spacing(2);
  height: 48px;

  .content {
    gap: spacing(2);
  }

  @include typography.body1;
  @include typography.semibold;

  &.link {
    padding: 0;
    height: auto;

    &[data-focus-visible] {
      border-radius: 2px;
      outline-offset: 4px;
    }

    &[data-hovered] {
      border-radius: 2px;
      outline: spacing(1) solid var(--button-hover-outline);
      outline-offset: 0;
    }
  }

  .icon {
    @include icon-size(md);
  }
}

.button:has(.icon):not(.contentAlignmentCenter, .contentAlignmentStart, .contentAlignmentEnd) .content {
  justify-content: space-between;
}

.contentAlignmentStart .content {
  justify-content: flex-start;
}

.contentAlignmentEnd .content {
  justify-content: flex-end;
}
